import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Briefcase, RefreshCw, AlertTriangle, TrendingUp, TrendingDown, Layers } from "lucide-react";


/**
 * Multi-broker portfolio view. Combined summary + per-account tabs with positions.
 * Hides itself when the user has no broker connections at all.
 */
export function PortfolioCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // connection_id

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portfolio/summary");
      setData(data);
      // Re-sync selected tab: if it's missing from the fresh account list, jump to the first.
      setSelected((prev) => {
        if (!data.accounts?.length) return null;
        const stillExists = data.accounts.some((a) => a.connection_id === prev);
        return stillExists ? prev : data.accounts[0].connection_id;
      });
    } catch (err) {
      console.debug("[portfolio] load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return null;
  if (!data.accounts?.length) return null;    // no broker → don't render anything

  const combined = data.combined;
  const active = data.accounts.find((a) => a.connection_id === selected) || data.accounts[0];
  const pnlClass = (v) => (v > 0 ? "text-[var(--profit)]" : v < 0 ? "text-[var(--loss)]" : "text-[var(--text-secondary)]");

  return (
    <section className="card-sharp p-5 mb-6" data-testid="portfolio-card">
      <div className="flex items-center gap-2 mb-4">
        <Briefcase size={14} className="text-[var(--accent)]"/>
        <h3 className="font-display text-lg text-white flex-1">Portfolio</h3>
        <span className="chip">{data.account_count} account{data.account_count === 1 ? "" : "s"}</span>
        <button data-testid="portfolio-refresh" onClick={load}
          className="p-1.5 border border-white/10 rounded hover:border-[var(--accent)]"><RefreshCw size={12}/></button>
      </div>

      {/* Combined summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCell label="Total equity"  value={fmtMoney(combined.equity)}   testId="portfolio-total-equity"/>
        <SummaryCell label="Total balance" value={fmtMoney(combined.balance)}  testId="portfolio-total-balance"/>
        <SummaryCell label="Open P&L" value={fmtMoney(combined.open_pnl)}
          valueClass={pnlClass(combined.open_pnl)} testId="portfolio-open-pnl"/>
        <SummaryCell label="Open trades" value={String(combined.positions_count)} testId="portfolio-open-trades"/>
      </div>

      {/* Account tabs */}
      {data.accounts.length > 1 && (
        <div className="flex gap-1 mb-3 overflow-x-auto pb-1" data-testid="portfolio-account-tabs">
          {data.accounts.map((a) => (
            <button key={a.connection_id}
              data-testid={`portfolio-tab-${a.connection_id}`}
              onClick={() => setSelected(a.connection_id)}
              className={`px-3 py-1.5 text-[10px] font-mono border rounded whitespace-nowrap ${
                selected === a.connection_id
                  ? "!border-[var(--accent)] !text-[var(--accent)] bg-[var(--accent)]/10"
                  : "border-white/10 text-[var(--text-secondary)] hover:border-[var(--accent)]/50"
              }`}>
              {a.label} · {a.provider}
            </button>
          ))}
        </div>
      )}

      {/* Selected account detail */}
      <div className="border border-white/10 rounded p-3 bg-black/30" data-testid="portfolio-account-detail">
        <div className="flex items-center gap-2 mb-2">
          <Layers size={12} className="text-[var(--text-muted)]"/>
          <span className="font-display text-white text-sm flex-1">{active.label}</span>
          <span className={`chip ${active.status === "connected" ? "!border-[var(--profit)] !text-[var(--profit)]" : "!border-[var(--warning)] !text-[var(--warning)]"}`}>
            {active.status}
          </span>
        </div>

        {active.error ? (
          <div className="flex items-start gap-2 text-[11px] font-mono text-[var(--warning)]"
            data-testid={`portfolio-account-err-${active.connection_id}`}>
            <AlertTriangle size={12} className="mt-0.5 shrink-0"/>
            <span>{active.error}</span>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <MiniCell label="Equity"   value={fmtMoney(active.equity, active.currency)}/>
              <MiniCell label="Balance"  value={fmtMoney(active.balance, active.currency)}/>
              <MiniCell label="Open P&L" value={fmtMoney(active.open_pnl, active.currency)} valueClass={pnlClass(active.open_pnl)}/>
            </div>

            {active.positions?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-[10px] font-mono">
                  <thead>
                    <tr className="text-[var(--text-muted)] border-b border-white/10">
                      <th className="text-left py-1">Symbol</th>
                      <th className="text-left">Side</th>
                      <th className="text-right">Vol</th>
                      <th className="text-right">Open</th>
                      <th className="text-right">P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.positions.map((p, idx) => (
                      <tr key={p.ticket || p.id || idx} className="border-b border-white/5">
                        <td className="py-1 text-white">{p.symbol}</td>
                        <td className={p.side === "buy" || p.side === "long" ? "text-[var(--profit)]" : "text-[var(--loss)]"}>
                          {(p.side || "").toUpperCase()}
                          {p.side === "buy" || p.side === "long" ? <TrendingUp size={9} className="inline ml-0.5"/> : <TrendingDown size={9} className="inline ml-0.5"/>}
                        </td>
                        <td className="text-right">{Number(p.volume || 0).toFixed(2)}</td>
                        <td className="text-right">{Number(p.open_price || 0).toFixed(5)}</td>
                        <td className={`text-right ${pnlClass(Number(p.profit || 0))}`}>
                          {fmtMoney(Number(p.profit || 0), active.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[10px] font-mono text-[var(--text-muted)] text-center py-3">No open positions</p>
            )}
          </>
        )}
      </div>

      <div className="text-[10px] font-mono text-[var(--text-muted)] mt-3 text-center">
        <Link to="/broker" className="hover:text-[var(--accent)] underline">Manage broker connections</Link>
      </div>
    </section>
  );
}


function SummaryCell({ label, value, valueClass = "text-white", testId }) {
  return (
    <div className="border border-white/10 rounded p-3 bg-black/20" data-testid={testId}>
      <div className="text-[9px] font-mono text-[var(--text-muted)] uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-display mt-1 ${valueClass}`}>{value}</div>
    </div>
  );
}

function MiniCell({ label, value, valueClass = "text-white" }) {
  return (
    <div>
      <div className="text-[9px] font-mono text-[var(--text-muted)] uppercase">{label}</div>
      <div className={`text-sm font-mono ${valueClass}`}>{value}</div>
    </div>
  );
}

function fmtMoney(n, ccy = "USD") {
  if (n === null || n === undefined) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  const val = abs >= 1000 ? abs.toLocaleString(undefined, { maximumFractionDigits: 0 })
                          : abs.toFixed(2);
  return `${sign}${val} ${ccy || ""}`.trim();
}
